import React from 'react'
import { useProfileTotals } from '@/hooks/useProfileTotals'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useI18n } from '@/contexts/I18nContext'
import MessageSquareTextIcon from '@/assets/icons/message-square-text.svg'
import ConnectionsIcon from '@/assets/icons/connections.svg'
import AwardIcon from '@/assets/icons/award.svg'
import GiftIcon from '@/assets/icons/gift.svg'

interface ProfileTotalsSliderProps {
  userId: string
  isOwnProfile: boolean
}

export const ProfileTotalsSlider = ({ userId }: ProfileTotalsSliderProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const { data: totals, isLoading, error } = useProfileTotals(userId)
  const isOwnProfile = user?.id === userId;

  if (isLoading) {
    return (
      <div className="mb-6">
        <div className="relative h-16">
          <div className="absolute left-1/2 transform -translate-x-1/2 w-screen overflow-x-auto pb-2 scrollbar-hide z-50">
            <div className="flex space-x-2 pl-4 pr-8">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-[100px] h-16 bg-gray-800 rounded-2xl animate-pulse"
                />
              ))}
              {/* Spacer element to ensure last card is fully visible */}
              <div className="flex-shrink-0 w-4"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !totals) {
    return <div className="mb-6 text-white/60 text-sm">{t('profile.unable_to_load_data')}</div>
  }

  const items = [
    {
      id: 'posts',
      label: t('profile.posts'),
      count: totals.posts_count || 0,
      icon: MessageSquareTextIcon,
      onClick: () => navigate(`/user/posts/${userId}`),
    },
    {
      id: 'connections',
      label: t('profile.connections'),
      count: totals.connections_count || 0,
      pending: totals.pending_connections_count || 0,
      icon: ConnectionsIcon,
      onClick: () => navigate(`/user/connections/${userId}`),
    },
    {
      id: 'achievements',
      label: t('profile.achievements'),
      count: totals.achievements_count || 0,
      icon: AwardIcon,
      onClick: () => navigate(`/user/achievements/${userId}`),
    },
    // Only show recognitions for own profile
    ...(isOwnProfile ? [{
      id: 'recognitions',
      label: t('profile.recognitions'),
      count: totals.recognitions_count || 0,
      icon: GiftIcon,
      onClick: () => navigate(`/user/recognitions/${userId}`),
    }] : []),
  ];

  return (
    <div className="mb-4">
      <div className="relative h-16">
        <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
          <div className="flex gap-1 pl-0">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex-shrink-0 min-w-[85px] w-[85px] px-2 py-2 flex flex-col justify-between cursor-pointer hover:bg-white/5 transition-colors relative"
                onClick={item.onClick}
              >
                {/* Pending badge for connections */}
                {(() => {
                  const shouldShow = item.pending && Number(item.pending) > 0 && item.id === 'connections';
                  return shouldShow ? (
                    <div className="absolute top-1 right-1 bg-[#D72E16] text-white text-[7px] font-bold px-1 py-0.5 rounded-full z-10 min-w-[14px] text-center">
                      {item.pending}
                    </div>
                  ) : null;
                })()}
                
                {/* Center content */}
                <div className="flex flex-col items-start justify-center flex-1">
                  <div className="text-white font-semibold text-[18px] mb-0.5">
                    {item.count.toString().padStart(2, '0')}
                  </div>
                  <div className="text-white/60 text-[10px] font-light text-center">
                    {item.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
} 